/**
 * Pretend restaurant kits — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tickets_play`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const pretendRestaurantKitsPack: ToilePack = {
    slug: 'pretend-restaurant-kits',
    category: 'classroom',
    name: 'Pretend restaurant kits',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'menu', label: 'Picture menu', subject: 'a children\'s restaurant menu with drawn dishes such as pizza, soup and juice, each with a price, under "MENU" at the top'},
        {key: 'order', label: 'Order pad', subject: 'a waiter\'s order pad page with a table number box, blank numbered lines for dishes and small tick boxes for drinks, labelled "ORDER"'},
        {key: 'bill', label: 'Bill', subject: 'a restaurant bill with lines for items and prices, a subtotal, a tip line and "THANK YOU" at the bottom'},
    ],
    styles: [
        {key: 'bistro', label: 'Bistro', style: 'a cosy bistro print with checked borders and handwritten-style capitals', palette: 'red and white check with black', framing: 'the piece centered on the page with a wide white margin'},
        {key: 'kid', label: 'Kids diner', style: 'a bright cartoon diner design with rounded dishes and chunky letters', palette: 'yellow, red and turquoise on white', framing: 'the piece centered on the page with a wide white margin'},
        {key: 'mono', label: 'Black and white', style: 'a simple black line print with large clear shapes and plain lettering', palette: 'black ink only', framing: 'the piece centered on the page with a wide white margin'},
    ],
    directions: ['pizza place', 'ice cream', 'cafe'],
    directionHint: 'try: pizza place',
    seo: {
        title: 'Pretend Restaurant Printables: Menu, Order Pad, Bill',
        metaDescription: 'Nine printable restaurant play pieces: a picture menu with prices, an order pad and a bill with a tip line, in three styles.',
        h1: 'Pretend restaurant printables: picture menu, order pad, bill',
        tagline: 'Nine play pieces, three kinds of restaurant paper, each in a bistro, kids diner or black-and-white style.',
        intro: "Children play waiter, cook and customer with a menu to read from and an order pad to write on. The printable pieces are a picture menu with prices, an order pad and a bill with a tip line. The nine pictures show a children's menu, an order pad and a bill, in red and white check bistro, bright cartoon diner and plain black line. Type a different restaurant, such as a pizza place, and the pieces follow.",
        useCases: [
            'a dramatic play restaurant in a preschool',
            'a home role-play with a menu and a bill',
            'a classroom reading and writing activity',
            'a restaurant game at a birthday party',
        ],
        faq: [
            {q: 'Is there a bill for the children to pay?', a: 'Yes. The bill has lines for items and prices, a subtotal, a tip line and a thank-you at the bottom.'},
            {q: 'Can I change the kind of restaurant?', a: 'Yes. Type the restaurant you want, such as a pizza place, and the pieces are drawn for that request, not as a fixed result.'},
            {q: 'What is the difference between the three styles?', a: 'Bistro is a cosy red and white check with handwritten-style capitals. Kids diner is bright yellow, red and turquoise with rounded dishes. Black and white is a simple black line print with large shapes.'},
        ],
    },
}
